import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { Report, Task, JobCardRequest } from '../types';
import { useAuth } from '../App';
import { Plus, FileText, Clock, CheckCircle2, XCircle, ClipboardList, ArrowRight, Send, Loader2, ChevronDown, ChevronUp, Sliders, Calendar } from 'lucide-react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { GenerateReportTrigger } from '../components/GenerateReportTrigger';
import { ServiceScheduleViewerModal } from '../components/ServiceScheduleViewerModal';

const TechnicianDashboard: React.FC = () => {
  const { profile } = useAuth();
  const [reports, setReports] = useState<Report[]>([]);
  const [jobCardRequests, setJobCardRequests] = useState<JobCardRequest[]>([]);
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isScheduleViewerOpen, setIsScheduleViewerOpen] = useState(false);

  useEffect(() => {
    if (!profile) return;

    // 1. Fetch reports
    const reportsQuery = query(collection(db, 'reports'), where('technicianId', '==', profile.uid), orderBy('createdAt', 'desc'));
    const unsubscribeReports = onSnapshot(reportsQuery, (snapshot) => {
      setReports(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Report)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'reports');
    });

    // 2. Fetch Jobcard requests
    const jcQuery = query(collection(db, 'job_card_requests'), where('requestedById', '==', profile.uid), orderBy('createdAt', 'desc'));
    const unsubscribeJC = onSnapshot(jcQuery, (snapshot) => {
      setJobCardRequests(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as JobCardRequest)));
      setLoading(false);
    }, (error) => {
      console.error('Error loading job card requests:', error);
      setLoading(false);
    });

    return () => {
      unsubscribeReports();
      unsubscribeJC();
    };
  }, [profile]);

  if (loading) return <div className="flex items-center justify-center h-64">Loading dashboard...</div>;

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">Technician Dashboard</h1>
          <p className="text-slate-500 font-medium italic">Manage your technical reports and tasks</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <GenerateReportTrigger />
          <button
            onClick={() => setIsScheduleViewerOpen(true)}
            className="flex items-center gap-2 px-5 py-3 bg-indigo-900 text-white rounded-2xl font-bold hover:bg-indigo-800 transition-all shadow-md shadow-indigo-900/20 active:scale-95 cursor-pointer text-xs uppercase tracking-wider"
          >
            <Calendar className="w-4 h-4 text-indigo-300" />
            <span>View Service Schedule</span>
          </button>
          <Link
            to="/reports?action=new_survey"
            className="flex items-center justify-center gap-2 bg-blue-800 text-white px-6 py-3 rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200 cursor-pointer text-sm"
          >
            <Plus className="w-5 h-5" />
            Generate Technical Survey Report
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600">
              <ClipboardList className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase">Total Reports</p>
              <p className="text-2xl font-black text-slate-900">{reports.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase">Approved</p>
              <p className="text-2xl font-black text-slate-900">
                {reports.filter(r => r.status === 'submitted_to_billing').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-orange-50 rounded-2xl flex items-center justify-center text-orange-600">
              <Clock className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase">Pending</p>
              <p className="text-2xl font-black text-slate-900">
                {reports.filter(r => r.status === 'submitted_to_technical' || r.status === 'arranged_by_technical').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <FileText className="w-6 h-6 text-blue-800" />
          Recent Reports
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reports.map(report => (
            <motion.div 
              key={report.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm hover:shadow-md transition-all group"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
                  <FileText className="w-5 h-5" />
                </div>
                <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                  report.status === 'submitted_to_billing' ? 'bg-emerald-50 text-emerald-600' :
                  report.status === 'rejected' ? 'bg-red-50 text-red-600' :
                  'bg-orange-50 text-orange-600'
                }`}>
                  {report.status.replace(/_/g, ' ')}
                </span>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-1 capitalize">{report.type} Report</h3>
              <p className="text-sm text-slate-500 mb-4">{report.clientName || 'Unknown Client'}</p>
              <div className="flex items-center justify-between pt-4 border-t border-slate-50">
                <span className="text-xs text-slate-400 font-medium">
                  {report.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                </span>
                <Link 
                  to="/reports" 
                  className="text-blue-800 text-sm font-bold flex items-center gap-1 hover:gap-2 transition-all"
                >
                  View Details
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          ))}
          {reports.length === 0 && (
            <div className="col-span-full py-12 text-center bg-slate-50 rounded-[2rem] border border-dashed border-slate-200">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3 opacity-50" />
              <p className="text-slate-500 font-bold italic">No reports found. Start by generating a new survey report.</p>
            </div>
          )}
        </div>
      </div>

      {/* Job Card Workflow Progress Tracker */}
      <div className="space-y-4 pt-6">
        <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Sliders className="w-6 h-6 text-orange-600" />
          Track Job Cards Progress
        </h2>
        
        {jobCardRequests.length === 0 ? (
          <div className="py-12 text-center bg-slate-50 rounded-[2rem] border border-dashed border-slate-200">
            <Sliders className="w-12 h-12 text-slate-300 mx-auto mb-3 opacity-50" />
            <p className="text-slate-500 font-bold italic">No Job Card Requests submitted yet.</p>
            <p className="text-xs text-slate-400 mt-1">Submit job card requests from the Technical module to track progress here.</p>
          </div>
        ) : (
          <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden divide-y divide-slate-100">
            {jobCardRequests.map((req) => {
              const isExpanded = expandedRequestId === req.id;
              return (
                <div key={req.id} className="p-6 transition-all">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <span className="text-base font-black text-slate-900">{req.clientName}</span>
                        <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-xl border ${
                          req.status === 'completed' 
                            ? "bg-emerald-50 text-emerald-600 border-emerald-100" 
                            : req.status === 'under_review'
                              ? "bg-blue-50 text-blue-600 border-blue-100"
                              : "bg-orange-50 text-orange-600 border-orange-100"
                        }`}>
                          {req.status === 'under_review' ? 'under review' : req.status}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 font-medium">
                        Branch: <span className="font-bold">{req.branchName || 'Main Branch'}</span> • Requested on: <span className="font-bold">{req.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}</span>
                      </p>
                    </div>

                    <div className="flex items-center gap-4">
                      {req.jobCardNo && (
                        <div className="bg-blue-50 border border-blue-100 px-3.5 py-1.5 rounded-xl font-mono text-xs font-black text-blue-950">
                          JC: {req.jobCardNo}
                        </div>
                      )}
                      <button
                        onClick={() => setExpandedRequestId(isExpanded ? null : req.id)}
                        className="p-2 text-slate-400 hover:text-blue-900 bg-slate-50 hover:bg-blue-50 rounded-xl transition-all cursor-pointer flex items-center gap-1.5 text-xs font-bold"
                      >
                        <span>{isExpanded ? 'Hide Tracker' : 'View Tracker'}</span>
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="mt-6 pt-6 border-t border-slate-50 space-y-6 text-sm"
                    >
                      {/* Workflow Progress Tracker */}
                      <div className="relative flex flex-col md:flex-row justify-between items-start md:items-center gap-6 md:gap-4 bg-slate-50/50 p-6 rounded-3xl border border-slate-100">
                        {/* Progress Connecting Line (Desktop) */}
                        <div className="absolute left-10 right-10 top-12 h-0.5 bg-slate-150 hidden md:block -z-0">
                          <div 
                            className="h-full bg-blue-955 transition-all duration-500"
                            style={{ 
                              width: req.status === 'completed' 
                                ? '100%' 
                                : req.status === 'under_review' 
                                  ? '50%' 
                                  : '0%' 
                            }}
                          />
                        </div>

                        {/* Step 1: Requested */}
                        <div className="flex items-center md:flex-col md:text-center gap-4 md:gap-3 flex-1 relative z-10 w-full">
                          <div className="w-12 h-12 rounded-full bg-blue-900 text-white flex items-center justify-center font-bold shadow-lg shadow-blue-900/10 border-4 border-white">
                            <Send className="w-5 h-5" />
                          </div>
                          <div>
                            <span className="text-xs font-black text-slate-900 block">1. Requested</span>
                            <span className="text-[10px] text-slate-400 font-bold block mt-0.5">
                              {req.createdAt?.toDate?.() ? `${req.createdAt.toDate().toLocaleDateString()} ${req.createdAt.toDate().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}` : 'N/A'}
                            </span>
                            <span className="text-[11px] text-slate-500 font-medium block mt-1">Submitted by Technical Dept</span>
                          </div>
                        </div>

                        {/* Step 2: Under Review */}
                        <div className="flex items-center md:flex-col md:text-center gap-4 md:gap-3 flex-1 relative z-10 w-full">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold border-4 border-white transition-all ${
                            req.status === 'completed' || req.status === 'under_review'
                              ? "bg-blue-900 text-white shadow-lg shadow-blue-900/10"
                              : "bg-slate-100 text-slate-400"
                          }`}>
                            {req.status === 'under_review' ? (
                              <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                              <Clock className="w-5 h-5" />
                            )}
                          </div>
                          <div>
                            <span className={`text-xs font-black block ${
                              req.status === 'completed' || req.status === 'under_review' ? "text-slate-900" : "text-slate-400"
                            }`}>2. Under Review</span>
                            <span className="text-[10px] text-slate-400 font-bold block mt-0.5">
                              {req.status === 'under_review' || req.status === 'completed' ? (
                                req.updatedAt?.toDate?.() ? (
                                  `${req.updatedAt.toDate().toLocaleDateString()} ${req.updatedAt.toDate().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`
                                ) : 'In Progress'
                              ) : 'Pending Review'}
                            </span>
                            <span className="text-[11px] text-slate-500 font-medium block mt-1">Billing preparing Job Card</span>
                          </div>
                        </div>

                        {/* Step 3: Completed */}
                        <div className="flex items-center md:flex-col md:text-center gap-4 md:gap-3 flex-1 relative z-10 w-full">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold border-4 border-white transition-all ${
                            req.status === 'completed'
                              ? "bg-emerald-600 text-white shadow-lg shadow-emerald-600/10"
                              : "bg-slate-100 text-slate-400"
                          }`}>
                            <CheckCircle2 className="w-5 h-5" />
                          </div>
                          <div>
                            <span className={`text-xs font-black block ${
                              req.status === 'completed' ? "text-slate-900" : "text-slate-400"
                            }`}>3. Completed</span>
                            <span className="text-[10px] text-slate-400 font-bold block mt-0.5">
                              {req.status === 'completed' ? (
                                req.updatedAt?.toDate?.() ? (
                                  `${req.updatedAt.toDate().toLocaleDateString()} ${req.updatedAt.toDate().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`
                                ) : 'Done'
                              ) : 'Awaiting Completion'}
                            </span>
                            <span className="text-[11px] text-slate-500 font-medium block mt-1">Linked and ready to view</span>
                          </div>
                        </div>
                      </div>

                      {/* Job Card Details Content (if completed) */}
                      {req.status === 'completed' && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Job Card Number</span>
                            <span className="text-base font-black text-blue-900">{req.jobCardNo}</span>
                          </div>
                          {req.proformaNo && (
                            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Proforma Details</span>
                              <span className="font-bold text-slate-800 block">No: {req.proformaNo}</span>
                              {req.proformaAmount && (
                                <span className="text-emerald-600 font-bold block">
                                  Amount: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'TZS', minimumFractionDigits: 0 }).format(req.proformaAmount).replace('TZS', 'TSh')}
                                </span>
                              )}
                            </div>
                          )}
                          <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Job Terms</span>
                            <span className="font-bold text-slate-800 block">{req.jobTerms || 'Standard Terms'}</span>
                            <span className="text-slate-500 block">Service: {req.isForService === 'True' ? 'Yes' : 'No'}</span>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ServiceScheduleViewerModal
        isOpen={isScheduleViewerOpen}
        onClose={() => setIsScheduleViewerOpen(false)}
      />
    </div>
  );
};

export default TechnicianDashboard;
